import {API} from '@/constants/api';
import {ACCESS_KEY} from '@/constants/keys';
import {createAxiosInstance} from './axiosConfig';
import {createStreamingChannel, fetchBlobFromApi} from './fetchConfig';
import axios from 'axios';

const defaultHeaders = {
  'Content-Type': 'application/json',
};
const multipartHeaders = {
  'Content-Type': 'multipart/form-data',
};
const authHeaders: {Authorization: string} = {Authorization: ''};

export function setAuthHeader(token: string) {
  authHeaders.Authorization = `Bearer ${token}`;
}

// this function is used to update the token for the authheader object in memory
function setAuthHeaderFromResponse(response: any) {
  setAuthHeader(response.data.access_token);
  return response;
}

setAuthHeader(localStorage.getItem(ACCESS_KEY) as string);

// This is for example
export async function demo() {
  return await createAxiosInstance({
    url: API.noAuthUrls.demo,
    method: 'GET',
    headers: {...defaultHeaders},
  });
}

export async function loginOtpRequest(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.loginOtp,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  });
}

export async function loginOtpVerify(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.loginVerifyOTP,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  });
}

export async function loginOtpResend(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.loginResendOTP,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  });
}

export async function loginMfaResend(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.loginResendMFA,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  });
}

export async function loginMfaVerify(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.loginVerifyMFA,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  }).then(setAuthHeaderFromResponse);
}

export async function signupOtpRequest(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.signupOtp,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  });
}

export async function specialistSignupOtpRequest(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.specialistSignupOtp,
    method: 'POST',
    headers: {...multipartHeaders},
    data,
  });
}

export async function signupOtpVerify(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.singupVerifyOTP,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  });
}

export async function signupOtpResend(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.signupResendOTP,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  });
}

export async function signupMfaResend(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.signupResendMFA,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  });
}

export async function signupMfaVerify(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.signupVerifyMFA,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  }).then(setAuthHeaderFromResponse);
}

export async function viewProfile() {
  return await createAxiosInstance({
    url: API.authUrls.profile,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function editProfile(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.profile,
    method: 'PATCH',
    headers: {...multipartHeaders, ...authHeaders},
    data,
  });
}

export async function contactQuery(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.contact,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  });
}

export async function getSpecialists(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialists,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getPatients(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.patients,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getAdmins(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.admins,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getAuditLogs(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.auditLogs,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getAllAccessModules() {
  return await createAxiosInstance({
    url: API.authUrls.adminAccessModulesAll,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function exportAuditLogs(data: any) {
  try {
    // Import encryption utilities if needed
    const {ENCRYPTION} = await import('@/constants');
    const {encryptPayload} = await import('@/utils/encryption');

    // Prepare data - encrypt if encryption is enabled
    let processedData = data;
    if (ENCRYPTION.encryptionFlag && Object.keys(data).length > 0) {
      processedData = encryptPayload(data);
    }

    const axiosInstance = axios.create({
      baseURL: API.currentEnv ? API.baseUrls[API.currentEnv as keyof typeof API.baseUrls] : API.baseUrls.dev,
      headers: {...defaultHeaders, ...authHeaders},
      responseType: 'blob',
    });

    return await axiosInstance({
      url: API.authUrls.auditLogsExport,
      method: 'POST',
      data: processedData,
    });
  } catch (error: any) {
    if (error?.response) {
      // Try to parse error message from blob if it's an error response
      if (error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          error.response.data = errorData;
        } catch {
          error.response.data = {status: 'error', status_code: 'E-10001'};
        }
      }
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
}

export async function addAdmin(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.admin,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function deleteAdmin(data: {id: number}) {
  return await createAxiosInstance({
    url: API.authUrls.admin,
    method: 'DELETE',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function updateAdmin(data: {id: number; name: string; email: string; status: boolean; scope: number}) {
  return await createAxiosInstance({
    url: API.authUrls.admin,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function updateUserRecord(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.user,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export function uploadHealthReport(data: any) {
  return createStreamingChannel({
    url: API.authUrls.patientConsultationAnalyzeReport,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function addConsultationReport(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.patientConsultationAddReport,
    method: 'POST',
    headers: {...multipartHeaders, ...authHeaders},
    data,
  });
}

export async function removeConsultationReport(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.patientConsultationRemoveReport,
    method: 'DELETE',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}
export async function verifyLicence(data: {license_number: string; license_year: string; email: string}) {
  return await createAxiosInstance({
    url: API.noAuthUrls.verifyLicence,
    method: 'POST',
    headers: {...defaultHeaders},
    data,
  });
}

export async function fetchSpecialization() {
  return await createAxiosInstance({
    url: API.noAuthUrls.appSettingsSpecialization,
    method: 'GET',
    headers: {...defaultHeaders},
  });
}

export async function fetchSpecialistList() {
  return await createAxiosInstance({
    url: API.noAuthUrls.appSettingsSpecialistList,
    method: 'GET',
    headers: {...defaultHeaders},
  });
}

export async function fetchCountryList() {
  return await createAxiosInstance({
    url: API.noAuthUrls.appSettingsCountryList,
    method: 'GET',
    headers: {...defaultHeaders},
  });
}

export async function selectSpecialist(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.patientConsultationSpecialist,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getConsultationSpecialists(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.patientConsultationSpecialist,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getConsultationPayment(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.patientConsultationPayment,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function profileOtpRequest(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.profileRequestOtp,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function profileOtpVerify(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.profileVerifyOtp,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function profileOtpResend(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.profileResendOtp,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getPatientSpecialists(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.patientSpecialist,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function addFavouriteDoctor(data: {specialist_id: number}) {
  return await createAxiosInstance({
    url: API.authUrls.patientFavouriteDoctor,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function removeFavouriteDoctor(data: {specialist_id: number}) {
  return await createAxiosInstance({
    url: API.authUrls.patientFavouriteDoctor,
    method: 'DELETE',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getRecentCases() {
  return await createAxiosInstance({
    url: API.authUrls.patientRecentCases,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function uploadHealthReportPublic(data: any) {
  return await createAxiosInstance({
    url: API.noAuthUrls.publicConsultationUpload,
    method: 'POST',
    headers: {...multipartHeaders},
    data,
  });
}

export async function newConsulationCheck() {
  return await createAxiosInstance({
    url: API.authUrls.newConsulationCheck,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function getPendingConsultationCase() {
  return await createAxiosInstance({
    url: API.authUrls.pendingConsultationcase,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function getConsultationList(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.consultationList,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export function uploadAdditionalConsultationFiles2(data: any) {
  return createStreamingChannel({
    url: API.authUrls.uploadAdditionalConsultationFiles2,
    method: 'POST',
    headers: {...authHeaders},
    data,
    isMultipart: true,
  });
}

export function uploadAdditionalConsultationFiles(data: any, cancelTokenSource?: any) {
  return createAxiosInstance({
    url: API.authUrls.uploadAdditionalConsultationFiles,
    method: 'POST',
    headers: {...multipartHeaders, ...authHeaders},
    data,
    cancelToken: cancelTokenSource?.token,
  });
}

export async function removeAdditionalConsultationDocument(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.removeAdditionalConsultationDocument,
    method: 'DELETE',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getConsultationSummary(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistConsultationSummary,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function submitFeedbackAndRating(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.submitFeedbackAndRating,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function cancelConsultation(data: any, signal?: AbortSignal) {
  return await createAxiosInstance({
    url: API.authUrls.cancelConsultation,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
    signal,
  });
}

export async function followUpConsultation(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.followUpConsultation,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getSpecialistDashboardStats() {
  return await createAxiosInstance({
    url: API.authUrls.specialistDashboardStats,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function getSpecialistConsultations(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistConsultationList,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getSpecialistPayments(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistPayments,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getPatientDetails(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistConsultationPatient,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function updateConsultationSummary(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistConsultationSummary,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function uploadSpecialistConsultationAdditionalDocuments(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistConsultationAdditionalDocuments,
    method: 'POST',
    headers: {...multipartHeaders, ...authHeaders},
    data,
  });
}
export async function removeSpecialistConsultationAdditionalDocuments(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistConsultationAdditionalDocuments,
    method: 'DELETE',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}
export async function specialistListForRefferal(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistListForRefferal,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}
export async function referralConsultation(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.referralConsultation,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function manualCategorySelection(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.manualCategorySelection,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getPaymentReceipt(consultationId: number) {
  return await fetchBlobFromApi(API.authUrls.getPaymentReceipt, {id: consultationId}, `payment_receipt_case_${consultationId}.pdf`);
}

export async function getDocumentBytes(file_id: number) {
  return await fetchBlobFromApi(API.authUrls.document, {id: file_id});
}

export async function getSecondOpinionReport(consultation_case_id: number) {
  return await fetchBlobFromApi(API.authUrls.secondOpinionReport, {id: consultation_case_id});
}

//==============================================//
// Specialist Profile APIs
//==============================================//
export async function getSpecialistProfile() {
  return await createAxiosInstance({
    url: API.authUrls.profile,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function updateSpecialistProfile(data: FormData) {
  // Create axios instance without encryption for this API
  return await createAxiosInstance({
    url: API.authUrls.profile,
    method: 'PATCH',
    headers: {...multipartHeaders, ...authHeaders},
    data,
  });
}

export async function submitSpecialistProfileApproval() {
  return await createAxiosInstance({
    url: API.authUrls.profileSubmitApproval,
    method: 'POST',
    headers: {
      ...defaultHeaders,
      ...authHeaders,
    },
  });
}

export async function removeLicense() {
  return await createAxiosInstance({
    url: `${API.authUrls.profile}/remove-license`,
    method: 'DELETE',
    headers: {
      ...defaultHeaders,
      ...authHeaders,
    },
  });
}

export async function removeSignature() {
  return await createAxiosInstance({
    url: `${API.authUrls.profile}/remove-signature`,
    method: 'DELETE',
    headers: {
      ...defaultHeaders,
      ...authHeaders,
    },
  });
}

export async function getSpecialistDetails(id: number) {
  return await createAxiosInstance({
    url: API.authUrls.specialistDetails,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params: {id},
  });
}

export async function getSpecialistFeedback(id: number) {
  return await createAxiosInstance({
    url: API.authUrls.specialistFeedback,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params: {id},
  });
}

export async function getChatMessages(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.apiChatMessages,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function sendChatMessage(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.apiChatMessages,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getWsToken() {
  return await createAxiosInstance({
    url: API.authUrls.wsToken,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function getAdminRoles(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.adminRoles,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getAdminAccessModules(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.adminAccessModules,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function updateRole(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.adminRoles,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getRoleFilterOptions() {
  return await createAxiosInstance({
    url: API.noAuthUrls.roleFilterOptions,
    method: 'GET',
    headers: {...defaultHeaders},
  });
}

export async function getFilters(params: {type: number}) {
  return await createAxiosInstance({
    url: API.authUrls.filters,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getSpecialistFilterOptions() {
  return await createAxiosInstance({
    url: API.noAuthUrls.appSettingsSpecialistFilterOptions,
    method: 'GET',
    headers: {...defaultHeaders},
  });
}

export async function getFilterList(params: {type: number}) {
  return await createAxiosInstance({
    url: API.authUrls.filters,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function addFilter(data: {type: number; label: string; min_value: number; max_value: number; status: boolean}) {
  return await createAxiosInstance({
    url: API.authUrls.filters,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function editFilter(data: {id: number; label: string; min_value: number; max_value: number; status: boolean}) {
  return await createAxiosInstance({
    url: API.authUrls.filters,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getCategoryList(params: {page?: number}) {
  return await createAxiosInstance({
    url: API.authUrls.category,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function addCategory(data: {name: string}) {
  return await createAxiosInstance({
    url: API.authUrls.category,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function editCategory(data: {id: number; name: string; order: number}) {
  return await createAxiosInstance({
    url: API.authUrls.category,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}
export async function patientSettingExport(data: {contact_email: string; reason?: string}) {
  return await createAxiosInstance({
    url: API.authUrls.patientSettingExport,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function patientDelete(data: {contact_email: string}) {
  return await createAxiosInstance({
    url: API.authUrls.patientDeletion,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function CancelPatientData() {
  return await createAxiosInstance({
    url: API.authUrls.CancelPatientData,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function getAdminPatientConsultations(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.adminPatientConsultations,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getPopularSpecialistList(params: {page?: number; search_text?: string; category?: number; auto_popular?: boolean}) {
  return await createAxiosInstance({
    url: API.authUrls.popularSpecialists,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getPopularityLimit() {
  return await createAxiosInstance({
    url: API.authUrls.popularityLimit,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function updatePopularityLimit(data: {limit: number}) {
  return await createAxiosInstance({
    url: API.authUrls.popularityLimit,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function overrideSpecialistPopularity(data: {id: number}) {
  return await createAxiosInstance({
    url: API.authUrls.overrideSpecialistPopularity,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getPatientAccountTickets(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.patientAccountTickets,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function exportPatientAccount(data: {id: number}) {
  return await createAxiosInstance({
    url: API.authUrls.patientAccountExport,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function deletePatientAccount(data: {id: number}) {
  return await createAxiosInstance({
    url: API.authUrls.patientAccountDeletion,
    method: 'DELETE',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function holdPatientAccountDeletion(data: {id: number}) {
  return await createAxiosInstance({
    url: API.authUrls.patientAccountHoldDeletion,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function resumePatientAccountDeletion(data: {id: number}) {
  return await createAxiosInstance({
    url: API.authUrls.patientAccountResumeDeletion,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getHospitalLevelAdminList(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalPointOfContact,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}
export async function updateHospitalPocStatus(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalPointOfContact,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}
export async function addHospitalPoc(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalPointOfContact,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function submitHelpRequest(data: {contact_email: string; question: string}) {
  return await createAxiosInstance({
    url: API.authUrls.profileHelp,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function submitHospitalHelpRequest(data: {contact_email: string; question: string}) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalHelp,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getHospitalProfile() {
  return await createAxiosInstance({
    url: API.authUrls.hospitalProfile,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function updateHospitalProfile(data: FormData) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalProfile,
    method: 'PATCH',
    headers: {...multipartHeaders, ...authHeaders},
    data,
  });
}

export async function toggleLicenceVerification(data: {mode: number}) {
  return await createAxiosInstance({
    url: API.authUrls.adminToggleLicenceVerification,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getLicenceVerificationMode() {
  return await createAxiosInstance({
    url: API.authUrls.adminGetLicenceVerificationMode,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function getSupportTickets(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.supportAdminTickets,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function sendReply(data: {id: number; subject: string; message: string}) {
  return await createAxiosInstance({
    url: API.authUrls.supportAdminReply,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function markAsSpam(data: {id: number}) {
  return await createAxiosInstance({
    url: API.authUrls.supportAdminSpam,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getHospitalSpecialists(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalSpecialists,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function inviteHospitalSpecialist(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalSpecialists,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function editHospitalSpecialist(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalSpecialists,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getSpecialistAdminConsultationList(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistAdminConsultationList,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getSpecialistOnboardingList(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistAdminOnboardingList,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function getSpecialistAdminProfile(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistAdminProfile,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function specialistAdminProfileRequestChange(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistAdminProfile,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function setSpecialistAdminProfileFeeAndVisibility(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistAdminProfile,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getFinancePayments(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.financePayments,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function refundPayment(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.financeRefundPayment,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getSpecialistPayouts(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.financeSpecialistPayouts,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function changePayoutStatus(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.financeChangePayoutStatus,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}
export async function validateSpecialistInvitation(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistInvitation,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function specialistBooking(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.specialistBooking,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function proceedWithAiRecommendedSpecialist(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.proceedWithAiRecommendedSpecialist,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function assignHospitalSpecialist(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.assignHospitalSpecialist,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getHospitalConsultationCaseList(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalConsultationList,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function assignHospitalSpecialistToConsultation(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalConsultationAssignSpecialist,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getHospitalSpecialistAll() {
  return await createAxiosInstance({
    url: API.authUrls.hospitalSpecialistAll,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}
export async function getHospitalDetails(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalDetails,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}
export async function getHospitalSpecialistsList(params: any) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalSpecialistsList,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function selectHospitalSpecialist(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.selectHospitalSpecialist,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getNotifications(params: {page: number}) {
  return await createAxiosInstance({
    url: API.authUrls.notifications,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
    params,
  });
}

export async function markAllNotificationsAsRead(payload?: {id?: number}) {
  return await createAxiosInstance({
    url: API.authUrls.markAllNotificationsAsRead,
    method: 'PATCH',
    headers: {...defaultHeaders, ...authHeaders},
    data: payload,
  });
}

export async function getHospitalList() {
  return await createAxiosInstance({
    url: API.authUrls.hospitalList,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function addConsultationQuery(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.addConsultationQuery,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function getHospitalDetailsFromEnrichment(data: any) {
  return await createAxiosInstance({
    url: API.authUrls.hospitalEnrichment,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
    data,
  });
}

export async function logout() {
  return await createAxiosInstance({
    url: API.authUrls.logout,
    method: 'POST',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

export async function accountTicketStatus() {
  return await createAxiosInstance({
    url: API.authUrls.accountTicketDetails,
    method: 'GET',
    headers: {...defaultHeaders, ...authHeaders},
  });
}

// Speech to Text API
export async function transcribeAudio(data: FormData) {
  return await createAxiosInstance({
    url: API.authUrls.speechTranscribe,
    method: 'POST',
    headers: {...multipartHeaders, ...authHeaders},
    data,
  });
}
